import { ApolloLink, concat, InMemoryCache, Observable } from '@apollo/client/core'
import { onError } from '@apollo/client/link/error'
import { RetryLink } from '@apollo/client/link/retry'
import { createUploadLink } from 'apollo-upload-client'
import { userService } from 'common/user-service.js'
import { useServerStatusStore } from 'src/stores/server-status'
import { useUserAuthStore } from 'src/stores/user/auth'
import { ALLOW_MIRROR } from 'src/common/config'
import { getGqlURL, getLocale } from 'src/common/functions'
import Platform from 'src/common/platform'
import { normalizeLocalizedData } from 'src/common/utils/locale'
import { getConsumerLocale } from 'src/core/locale/runtime'
import { trackHomeStartupGraphqlRequest } from 'src/services/perf/home-observability'

const cache = new InMemoryCache({
  typePolicies: {
    Query: {
      fields: {
        siteInfo: {
          merge(existing, incoming) {
            return { ...existing, ...incoming } // Объединяем данные
          },
        },
        bigWin: {
          merge(existing = {}, incoming) {
            return {
              ...existing,
              ...incoming,
            }
          },
        },
        userLootboxes: {
          merge(existing = {}, incoming) {
            return {
              ...existing,
              ...incoming,
            }
          },
        },
      },
    },
  },
})

const TOKEN_INVALID_SET = new Set(['token invalidated'])

export function getClientOptions(isGoBackend = false) {
  const config = {}
  const userAuthStore = useUserAuthStore()
  const serverStatusStore = useServerStatusStore()

  const uri = getGqlURL(isGoBackend)

  const httpLink = createUploadLink({
    uri: ({ operationName, query, variables }) => {
      const operationDefinition = query?.definitions?.find((definition) => definition.kind === 'OperationDefinition')
      const hasPropsContentField = operationDefinition?.selectionSet?.selections?.some(
        (selection) =>
          selection.kind === 'Field' && ['propsContent', 'propsContentList'].includes(selection.name?.value),
      )
      const contentType = hasPropsContentField && typeof variables?.type === 'string' ? variables.type : null
      const contentTypeParam = contentType ? `&type=${encodeURIComponent(contentType)}` : ''

      return `${uri}?${operationName}${contentTypeParam}`
    },
  })

  const authMiddleware = new ApolloLink((operation, forward) => {
    const token = userAuthStore.getToken
    const JWT = typeof token === 'string' && process.env.PLATFORM !== 'olimpbet' ? `JWT ${token}` : null
    const locale = getConsumerLocale({
      consumer: 'apollo',
      profileLocale: userAuthStore.getUserLanguage,
      getFallbackLocale: getLocale,
    })

    const headers = {
      'client-timezone': Intl.DateTimeFormat().resolvedOptions().timeZone,
      accept: 'application/json',
      'Accept-Language': locale,
    }

    if (window.__E2E__) {
      headers['x-e2e-test'] = 1
    }

    if (JWT && operation.operationName !== 'refreshToken') {
      headers.Authorization = JWT
    }

    const allowMirror = ALLOW_MIRROR.storage.getItem(ALLOW_MIRROR.query)
    if (JWT && allowMirror && operation.operationName === 'siteInfo') {
      headers[ALLOW_MIRROR.header] = 1
    }

    if (Platform.is.mobile) {
      headers.CASISMOBILE = 1
    }

    operation.setContext({ headers })
    return forward(operation)
  })

  const homePerfMiddleware = new ApolloLink((operation, forward) => {
    const start = typeof performance !== 'undefined' ? performance.now() : Date.now()
    const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now())

    return new Observable((observer) => {
      const subscription = forward(operation).subscribe({
        next(result) {
          observer.next(result)
        },
        error(error) {
          trackHomeStartupGraphqlRequest(now() - start)
          observer.error(error)
        },
        complete() {
          trackHomeStartupGraphqlRequest(now() - start)
          observer.complete()
        },
      })

      return () => {
        subscription.unsubscribe()
      }
    })
  })

  const localeNormalizationMiddleware = new ApolloLink((operation, forward) => {
    return new Observable((observer) => {
      const subscription = forward(operation).subscribe({
        next(result) {
          const normalizedData = normalizeLocalizedData(result.data)
          observer.next(normalizedData === result.data ? result : { ...result, data: normalizedData })
        },
        error(error) {
          observer.error(error)
        },
        complete() {
          observer.complete()
        },
      })

      return () => subscription.unsubscribe()
    })
  })

  function fullLogout() {
    void userService.logout().finally(() => {
      location.reload()
    })
  }

  const errorLink = onError(({ graphQLErrors, operation, networkError }) => {
    if (graphQLErrors) {
      const variables = Object.entries(operation.variables)
        .map(([key, value]) => `${key}:${value}`)
        .join(';')

      const description = `(${operation.operationName}${variables ? ' | ' + variables : ''})`
      const errors = graphQLErrors.map(({ message }) => message)

      console.error(`GQL errors: ${description}: ${errors.join(' | ')}`)
      if (errors.includes('Invalid payload')) {
        void fullLogout()
        return
      }
    }

    if (networkError) {
      const isInvalidPayloadError = networkError?.message?.includes('Invalid payload')
      if (isInvalidPayloadError) {
        console.warn(`Network error [${operation.operationName}]. Invalid payload`)
        void fullLogout()
        return
      }

      console.error(`Network error [${operation.operationName}]. ${networkError.message}`)

      const res = networkError.result
      if (TOKEN_INVALID_SET.has(res?.err) || networkError.statusCode === 401) {
        void fullLogout()
      }
    }
  })

  const serverCheckMiddleware = new ApolloLink((operation, forward) => {
    const IGNORE_LIST = ['getUnderdev']
    if (serverStatusStore.serverIsOk || IGNORE_LIST.includes(operation.operationName)) {
      return forward(operation)
    }

    return new Observable((observer) => {
      observer.error(new Error('Server is not available'))
    })
  })

  const BAD_STATUSES = [502, 503, 504]
  const MAX_ERRORS = 4
  const TIME_WINDOW = 10000
  const errorTimestamps = []
  let blockedRetry = false

  const NON_IDEMPOTENT_MUTATIONS = new Set(['userLootboxCraftBatch'])

  const retryLink = new RetryLink({
    delay: {
      initial: 4000,
      max: Infinity,
      jitter: true,
    },
    attempts: {
      max: 2,
      retryIf: (error, operation) => {
        if (blockedRetry) {
          return false
        }

        if (NON_IDEMPOTENT_MUTATIONS.has(operation.operationName)) {
          return false
        }

        const status = error.statusCode
        const res = error.result
        if (TOKEN_INVALID_SET.has(res?.err) || status === 401) {
          return false
        }

        if (BAD_STATUSES.includes(error.statusCode)) {
          const now = Date.now()
          errorTimestamps.push(now)

          const cutoffTime = now - TIME_WINDOW
          while (errorTimestamps.length > 0 && errorTimestamps[0] < cutoffTime) {
            errorTimestamps.shift()
          }

          if (errorTimestamps.length >= MAX_ERRORS) {
            blockedRetry = true
            serverStatusStore.updateServerIsOk(false)
            return false
          }
        }
        return !!error
      },
    },
  })

  config.cache = cache

  config.link = ApolloLink.from([
    serverCheckMiddleware,
    retryLink,
    homePerfMiddleware,
    errorLink,
    localeNormalizationMiddleware,
    concat(authMiddleware, httpLink),
  ])

  return config
}
